
import { Card, CardContent } from "@/components/ui/card";
import { User } from "@supabase/supabase-js";
import { ShareButton } from "./ShareButton";
import { useFavoriteToggle } from "@/hooks/useFavoriteToggle";
import { useLocalFavorites } from "@/hooks/useLocalFavorites";
import { VacationImage } from "./vacation-card/VacationImage";
import { VacationInfo } from "./vacation-card/VacationInfo";
import { VacationBadges } from "./vacation-card/VacationBadges";
import { VacationPrice } from "./vacation-card/VacationPrice";
import { VacationActionButton } from "./vacation-card/VacationActionButton";

interface VacationCardProps {
  vacation: any;
  user: User | null;
  isFavorite?: boolean;
  onFavoriteChange?: () => void;
  position?: number; // Position in search results for tracking
  searchQuery?: string; // Current search query for tracking
}

export const VacationCard = ({ 
  vacation, 
  user, 
  isFavorite = false, 
  onFavoriteChange,
  position,
  searchQuery
}: VacationCardProps) => {
  const { handleFavoriteToggle, isTogglingFavorite } = useFavoriteToggle(user, onFavoriteChange);
  const { toggleFavorite, isFavorite: isLocalFavorite } = useLocalFavorites();

  // Determine if this vacation is favorited (either via account or localStorage)
  const isVacationFavorite = user ? isFavorite : isLocalFavorite(vacation['product ID']);

  const onFavoriteToggleClick = () => {
    if (user) {
      // Use account-based favorites
      handleFavoriteToggle(vacation, isFavorite);
    } else {
      // Use localStorage favorites
      toggleFavorite(vacation['product ID']);
      onFavoriteChange?.();
    }
  };

  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-[1.02] flex flex-col h-full">
      <VacationImage
        vacation={vacation}
        user={user}
        isFavorite={isVacationFavorite}
        onFavoriteToggle={onFavoriteToggleClick}
        isTogglingFavorite={isTogglingFavorite}
      />
      
      <CardContent className="p-4 flex flex-col flex-grow">
        <div className="space-y-3 flex-grow">
          <div className="flex items-start justify-between">
            <div className="flex-1 mr-2">
              <VacationInfo vacation={vacation} />
            </div>
            <ShareButton vacation={vacation} />
          </div>
          
          <VacationBadges vacation={vacation} />
          
          <VacationPrice vacation={vacation} />
        </div>
        
        <VacationActionButton 
          vacation={vacation}
          searchQuery={searchQuery}
          position={position}
        />
      </CardContent>
    </Card>
  );
};
